import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wand2, RefreshCw, Plus, Trash2, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { callClaude, parseJsonResponse } from '../lib/claude'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const CURRENCIES = ['USD','GBP','EUR','THB','MYR','AUD','CAD','SGD','AED','NOK','SEK','CHF']

export default function QuickInvoice() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [prompt, setPrompt] = useState('')
  const [parsing, setParsing] = useState(false)
  const [parsed, setParsed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [error, setError] = useState('')
  const [clientInfo, setClientInfo] = useState({ name:'', email:'', country:'', city:'' })
  const [lineItems, setLineItems] = useState([{ description:'', qty:1, unit_price:'', show_price:true }])
  const [invoiceSettings, setInvoiceSettings] = useState({
    currency: 'USD',
    show_unit_price: true,
    show_total: true,
    discount: '',
    discount_type: 'fixed',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    notes: '',
    total_override: '',
  })

  async function parseWithClaude() {
    if (!prompt.trim()) return
    setParsing(true)
    setError('')
    try {
      const data = await callClaude({
          model: 'claude-opus-4-5',
          max_tokens: 1000,
          messages: [{
            role: 'user',
            content: `You are a tailor's invoice assistant. Parse this invoice request and return ONLY valid JSON.

Input: "${prompt}"

Return this exact JSON:
{
  "client": { "name": "", "email": "", "country": "", "city": "" },
  "currency": "USD",
  "line_items": [{ "description": "", "qty": 1, "unit_price": null, "show_price": true }],
  "total_only": null,
  "discount": null,
  "discount_type": "fixed",
  "notes": "",
  "show_unit_price": true,
  "show_total": true
}

Rules:
- If only a grand total is given with no per-item prices, set total_only to the number and set show_unit_price to false
- If separate prices per item are given, fill unit_price for each and set show_unit_price to true
- Currency: Norway/NOK, UK/GBP, Europe/EUR, Thailand/THB, Malaysia/MYR, Australia/AUD, default USD
- Parse quantities carefully: "2 pants" means qty=2, description="Pant". "10 shirts" means qty=10, description="Shirt". Always extract the number as qty and the item name as description (singular form, capitalised first letter)
- Keep item descriptions exactly as the user typed them otherwise — never rename or translate
- Return ONLY the JSON, nothing else`
          }]
      })
      const result = parseJsonResponse(data)

      setClientInfo({
        name: result.client?.name || '',
        email: result.client?.email || '',
        country: result.client?.country || '',
        city: result.client?.city || '',
      })
      setLineItems(result.line_items?.map(item => ({
        description: item.description || '',
        qty: item.qty || 1,
        unit_price: item.unit_price !== null ? String(item.unit_price) : '',
        show_price: item.show_price !== false,
      })) || [{ description:'', qty:1, unit_price:'', show_price:true }])
      setInvoiceSettings(prev => ({
        ...prev,
        currency: result.currency || 'USD',
        show_unit_price: result.show_unit_price !== false,
        show_total: result.show_total !== false,
        discount: result.discount ? String(result.discount) : '',
        discount_type: result.discount_type || 'fixed',
        notes: result.notes || '',
        total_override: result.total_only ? String(result.total_only) : '',
      }))
      setParsed(true)
    } catch(err) {
      setError('Could not parse (' + (err.message || 'error') + '). Try rephrasing or fill in manually.')
      setParsed(true)
    }
    setParsing(false)
  }

  function setItem(i, k, v) { setLineItems(p => p.map((item,idx) => idx===i ? {...item,[k]:v} : item)) }
  function addItem() { setLineItems(p => [...p, { description:'', qty:1, unit_price:'', show_price:true }]) }
  function removeItem(i) { setLineItems(p => p.filter((_,idx) => idx!==i)) }
  function setSetting(k, v) { setInvoiceSettings(p => ({...p,[k]:v})) }

  const cur = invoiceSettings.currency  // always use text e.g. "THB"
  const hasUnitPrices = invoiceSettings.show_unit_price && lineItems.some(i => i.unit_price !== '')
  const subtotal = hasUnitPrices
    ? lineItems.reduce((sum,i) => sum + (Number(i.qty||0) * Number(i.unit_price||0)), 0)
    : Number(invoiceSettings.total_override || 0)
  const discountAmt = invoiceSettings.discount_type === 'percent'
    ? subtotal * Number(invoiceSettings.discount||0) / 100
    : Number(invoiceSettings.discount||0)
  const total = (invoiceSettings.total_override && !hasUnitPrices)
    ? Number(invoiceSettings.total_override)
    : Math.max(0, subtotal - discountAmt)

  function generatePDF() {
    const doc = new jsPDF({ format: 'a4' })
    const pageW = doc.internal.pageSize.getWidth()

    // Header
    doc.setFontSize(22)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(26, 14, 8)
    doc.text("Jackie's Custom Tailor", 14, 20)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120, 100, 80)
    doc.text('Est. Bangkok, Thailand  1969  Bespoke Tailoring', 14, 27)

    // Invoice info top right
    doc.setTextColor(80)
    doc.setFontSize(10)
    doc.text(`Date: ${invoiceSettings.issue_date}`, pageW - 14, 16, { align: 'right' })
    if (invoiceSettings.due_date) doc.text(`Due: ${invoiceSettings.due_date}`, pageW - 14, 22, { align: 'right' })

    // Gold divider
    doc.setDrawColor(184, 150, 62)
    doc.setLineWidth(0.6)
    doc.line(14, 31, pageW - 14, 31)

    // Bill to
    let y = 40
    if (clientInfo.name) {
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text('BILL TO', 14, y)
      y += 5
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(26, 14, 8)
      doc.text(clientInfo.name, 14, y)
      y += 6
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.setTextColor(80)
      if (clientInfo.email) { doc.text(clientInfo.email, 14, y); y += 5 }
      const loc = [clientInfo.city, clientInfo.country].filter(Boolean).join(', ')
      if (loc) { doc.text(loc, 14, y); y += 5 }
      y += 4
    }

    // Build table columns — only show Amount column if unit prices are given
    const columns = ['Description', 'Qty']
    if (hasUnitPrices) columns.push(`Unit Price (${cur})`)
    columns.push(hasUnitPrices ? `Amount (${cur})` : '')  // empty header if total-only

    // If total only — don't show amount column at all
    const showAmountCol = hasUnitPrices

    const finalColumns = showAmountCol
      ? ['Description', 'Qty', `Unit Price (${cur})`, `Amount (${cur})`]
      : ['Description', 'Qty']

    const rows = lineItems.map(item => {
      const amt = Number(item.qty||0) * Number(item.unit_price||0)
      if (showAmountCol) {
        return [
          item.description || '—',
          String(item.qty),
          item.show_price && item.unit_price ? `${Number(item.unit_price).toLocaleString()} ${cur}` : '—',
          item.show_price && item.unit_price ? `${amt.toLocaleString()} ${cur}` : '—',
        ]
      }
      return [item.description || '—', String(item.qty)]
    })

    doc.autoTable({
      head: [finalColumns],
      body: rows,
      startY: y,
      margin: { left: 14, right: 14 },
      styles: { fontSize: 10, cellPadding: 4, overflow: 'linebreak' },
      headStyles: {
        fillColor: [26, 14, 8],
        textColor: [184, 150, 62],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: { fillColor: [250, 247, 242] },
      columnStyles: showAmountCol
        ? { 0: { cellWidth: 'auto' }, 1: { cellWidth: 20, halign: 'center' }, 2: { cellWidth: 45, halign: 'right' }, 3: { cellWidth: 45, halign: 'right' } }
        : { 0: { cellWidth: 'auto' }, 1: { cellWidth: 20, halign: 'center' } }
    })

    const finalY = doc.lastAutoTable.finalY + 6

    // Total section
    if (invoiceSettings.show_total) {
      doc.setDrawColor(184, 150, 62)
      doc.setLineWidth(0.4)
      doc.line(pageW - 80, finalY, pageW - 14, finalY)

      let ty = finalY + 7
      if (invoiceSettings.discount && Number(invoiceSettings.discount) > 0) {
        doc.setFontSize(9)
        doc.setTextColor(100)
        doc.text(`Subtotal: ${subtotal.toLocaleString()} ${cur}`, pageW - 14, ty, { align: 'right' })
        ty += 5
        doc.setTextColor(180, 50, 50)
        doc.text(`Discount: -${discountAmt.toLocaleString()} ${cur}`, pageW - 14, ty, { align: 'right' })
        ty += 5
      }
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(26, 14, 8)
      doc.text(`Total: ${total.toLocaleString()} ${cur}`, pageW - 14, ty, { align: 'right' })
    }

    // Notes
    if (invoiceSettings.notes) {
      const ny = doc.lastAutoTable.finalY + (invoiceSettings.show_total ? 42 : 12)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(120)
      doc.text('Notes:', 14, ny)
      const splitNotes = doc.splitTextToSize(invoiceSettings.notes, pageW - 28)
      doc.text(splitNotes, 14, ny + 5)
    }

    // Footer
    doc.setFontSize(8)
    doc.setTextColor(160)
    doc.text("Thank you for your business — Jackie's Custom Tailor, Bangkok", pageW / 2, 285, { align: 'center' })

    doc.save(`Invoice-${clientInfo.name || 'Client'}-${invoiceSettings.issue_date}.pdf`)
  }

  async function saveInvoice(status = 'draft') {
    setSaving(true)
    let clientId = null
    if (clientInfo.name || clientInfo.email) {
      // Link only when one client clearly matches (email, or a close full-name match).
      const { data: cands } = await supabase.rpc('match_clients', {
        p_name: clientInfo.name || null, p_email: clientInfo.email || null,
        p_city: clientInfo.city || null, p_limit: 3,
      })
      const [top, second] = cands || []
      const confident = top && (top.reasons?.includes('email') || top.reasons?.includes('name'))
        && (!second || second.score <= top.score - 15)
      if (confident) clientId = top.id
    }
    const { error } = await supabase.from('invoices').insert({
      client_id: clientId,
      line_items: lineItems,
      show_unit_price: invoiceSettings.show_unit_price,
      show_total: invoiceSettings.show_total,
      subtotal,
      discount: Number(invoiceSettings.discount || 0),
      discount_type: invoiceSettings.discount_type,
      total,
      currency: invoiceSettings.currency,
      status,
      issue_date: invoiceSettings.issue_date,
      due_date: invoiceSettings.due_date || null,
      notes: invoiceSettings.notes,
      created_by: profile?.id,
    })
    setSaving(false)
    if (!error) navigate('/invoices')
    else alert('Error: ' + error.message)
  }

  return (
    <div>
      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <h1 className="page-title">Quick Invoice</h1>
        {parsed && (
          <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowPreview(p => !p)}>
            {showPreview ? <><EyeOff size={15}/> Edit</> : <><Eye size={15}/> Preview</>}
          </button>
        )}
      </div>

      {/* Prompt */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div className="form-section-title">Describe the Invoice</div>
        <textarea
          className="form-textarea" rows={5} value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder={`Type naturally, e.g:\n\nKenneth Simso\n1 Jacket\n2 Pants\n10 Shirts\nTotal 46300\nNorway`}
          style={{ fontSize: '15px', lineHeight: '1.6' }}
        />
        <button className="btn btn-primary btn-lg" onClick={parseWithClaude} disabled={parsing || !prompt.trim()} style={{ borderRadius: '12px', marginTop: '8px' }}>
          {parsing ? <><RefreshCw size={18} style={{ animation: 'spin 1s linear infinite' }}/> Parsing…</> : <><Wand2 size={18}/> Generate Invoice</>}
        </button>
        {error && <p style={{ fontSize: '13px', color: 'var(--red)', marginTop: '8px' }}>{error}</p>}
        {parsed && !error && <p style={{ fontSize: '12px', color: 'var(--green)', marginTop: '6px' }}>✓ Done — review and edit below</p>}
      </div>

      {parsed && !showPreview && (
        <>
          {/* Client */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Client</div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Name</label><input className="form-input" value={clientInfo.name} onChange={e => setClientInfo(p => ({...p, name: e.target.value}))} placeholder="Client name"/></div>
              <div className="form-group"><label className="form-label">Country</label><input className="form-input" value={clientInfo.country} onChange={e => setClientInfo(p => ({...p, country: e.target.value}))} placeholder="Country"/></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Email <span>(optional)</span></label><input className="form-input" value={clientInfo.email} onChange={e => setClientInfo(p => ({...p, email: e.target.value}))} placeholder="email@example.com"/></div>
              <div className="form-group"><label className="form-label">City <span>(optional)</span></label><input className="form-input" value={clientInfo.city} onChange={e => setClientInfo(p => ({...p, city: e.target.value}))} placeholder="City"/></div>
            </div>
          </div>

          {/* Settings */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Settings</div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Currency</label>
                <select className="form-select" value={invoiceSettings.currency} onChange={e => setSetting('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Date</label>
                <input className="form-input" type="date" value={invoiceSettings.issue_date} onChange={e => setSetting('issue_date', e.target.value)}/>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={invoiceSettings.show_unit_price} onChange={e => setSetting('show_unit_price', e.target.checked)} style={{ accentColor: 'var(--brown-dark)' }}/>
                Show unit prices
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={invoiceSettings.show_total} onChange={e => setSetting('show_total', e.target.checked)} style={{ accentColor: 'var(--brown-dark)' }}/>
                Show total
              </label>
            </div>
          </div>

          {/* Line items */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Line Items</div>
            {lineItems.map((item, i) => (
              <div key={i} style={{ border: '1px solid var(--gray-100)', borderRadius: '8px', padding: '12px', marginBottom: '10px', background: 'var(--gray-50)' }}>
                <div className="form-group"><label className="form-label">Description</label>
                  <input className="form-input" value={item.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="e.g. Bespoke Suit"/>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">Qty</label>
                    <input className="form-input" type="number" min="1" value={item.qty} onChange={e => setItem(i, 'qty', e.target.value)}/>
                  </div>
                  <div className="form-group"><label className="form-label">Unit Price <span>(optional)</span></label>
                    <input className="form-input" type="number" step="0.01" value={item.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)} placeholder="Leave blank if total only"/>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={item.show_price} onChange={e => setItem(i, 'show_price', e.target.checked)} style={{ accentColor: 'var(--brown-dark)' }}/>
                    Show price on invoice
                  </label>
                  {lineItems.length > 1 && <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(i)}><Trash2 size={13}/></button>}
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-outline" onClick={addItem} style={{ width: '100%' }}><Plus size={16}/> Add Item</button>
          </div>

          {/* Total & discount */}
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="form-section-title">Total & Discount</div>
            <div className="form-group">
              <label className="form-label">Total Amount <span>(use if no unit prices given)</span></label>
              <input className="form-input" type="number" step="0.01" value={invoiceSettings.total_override} onChange={e => setSetting('total_override', e.target.value)} placeholder="e.g. 46300"/>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Discount <span>(optional)</span></label>
                <input className="form-input" type="number" step="0.01" value={invoiceSettings.discount} onChange={e => setSetting('discount', e.target.value)} placeholder="0"/>
              </div>
              <div className="form-group"><label className="form-label">Type</label>
                <select className="form-select" value={invoiceSettings.discount_type} onChange={e => setSetting('discount_type', e.target.value)}>
                  <option value="fixed">Fixed ({cur})</option>
                  <option value="percent">Percent (%)</option>
                </select>
              </div>
            </div>
            <div style={{ background: 'var(--brown-dark)', borderRadius: '10px', padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: '700', color: 'var(--gold)' }}>
                <span>Total</span>
                <span>{total.toLocaleString()} {cur}</span>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px', marginBottom: 0 }}>
              <label className="form-label">Notes <span>(shown on invoice)</span></label>
              <textarea className="form-textarea" rows={2} value={invoiceSettings.notes} onChange={e => setSetting('notes', e.target.value)} placeholder="Payment instructions, thank you note…"/>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '24px' }}>
            <button className="btn btn-outline" onClick={generatePDF} style={{ borderRadius:'10px', flexDirection:'column', gap:'4px', padding:'12px 8px', height:'60px' }}>
              <span style={{ fontSize: '18px' }}>↓</span><span style={{ fontSize: '11px' }}>PDF</span>
            </button>
            <button className="btn btn-outline" onClick={() => saveInvoice('draft')} disabled={saving} style={{ borderRadius:'10px', flexDirection:'column', gap:'4px', padding:'12px 8px', height:'60px' }}>
              <span style={{ fontSize: '18px' }}>💾</span><span style={{ fontSize: '11px' }}>Draft</span>
            </button>
            <button className="btn btn-primary" onClick={() => saveInvoice('sent')} disabled={saving} style={{ borderRadius:'10px', flexDirection:'column', gap:'4px', padding:'12px 8px', height:'60px' }}>
              <span style={{ fontSize: '18px' }}>✓</span><span style={{ fontSize: '11px' }}>{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </>
      )}

      {/* Preview */}
      {parsed && showPreview && (
        <div className="invoice-preview fade-in">
          <div className="invoice-header">
            <div>
              <div className="invoice-logo">Jackie's Custom Tailor</div>
              <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '2px' }}>Est. Bangkok · 1969</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '12px', color: 'var(--gray-500)' }}>
              <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--gray-900)' }}>INVOICE</div>
              <div>{invoiceSettings.issue_date}</div>
            </div>
          </div>

          {clientInfo.name && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', color: 'var(--gray-400)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Bill To</div>
              <div style={{ fontWeight: '600', fontSize: '15px' }}>{clientInfo.name}</div>
              {clientInfo.email && <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>{clientInfo.email}</div>}
              {(clientInfo.city || clientInfo.country) && <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>{[clientInfo.city, clientInfo.country].filter(Boolean).join(', ')}</div>}
            </div>
          )}

          <table className="invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'center', width: '40px' }}>Qty</th>
                {hasUnitPrices && <th style={{ textAlign: 'right' }}>Unit Price</th>}
                {hasUnitPrices && <th style={{ textAlign: 'right' }}>Amount</th>}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={i}>
                  <td>{item.description || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{item.qty}</td>
                  {hasUnitPrices && <td style={{ textAlign: 'right' }}>{item.show_price && item.unit_price ? `${Number(item.unit_price).toLocaleString()} ${cur}` : '—'}</td>}
                  {hasUnitPrices && <td style={{ textAlign: 'right' }}>{item.show_price && item.unit_price ? `${(Number(item.qty||0)*Number(item.unit_price||0)).toLocaleString()} ${cur}` : '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>

          {invoiceSettings.show_total && (
            <div style={{ borderTop: '2px solid var(--gray-200)', paddingTop: '12px', textAlign: 'right' }}>
              {invoiceSettings.discount && Number(invoiceSettings.discount) > 0 && (
                <>
                  <div style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '4px' }}>Subtotal: {subtotal.toLocaleString()} {cur}</div>
                  <div style={{ fontSize: '13px', color: 'var(--red)', marginBottom: '4px' }}>Discount: -{discountAmt.toLocaleString()} {cur}</div>
                </>
              )}
              <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--brown-dark)' }}>{total.toLocaleString()} {cur}</div>
            </div>
          )}

          {invoiceSettings.notes && <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--gray-500)', fontStyle: 'italic' }}>{invoiceSettings.notes}</p>}

          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => saveInvoice('sent')} disabled={saving}>{saving ? 'Saving…' : '✓ Save Invoice'}</button>
            <button className="btn btn-outline" onClick={generatePDF}>↓ PDF</button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  )
}
